import { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Loader2, Award, AlertCircle, CheckCircle2, ArrowRight, ClipboardCheck, FileDown, Package } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Dashboard de proyectos PG-07 en Completado y estado de su ficha de cierre. */
export default function CierreDashboardPage() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState({});
  const [zipping, setZipping] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/pg07/cierre-dashboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data) return null;

  const rows = (data.rows || []).filter(r => {
    if (filter === "needs_action") return r.needs_action;
    if (filter === "complete") return r.cierre_pct === 100;
    return true;
  });

  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const downloadZip = async () => {
    if (!selectedIds.length) return;
    setZipping(true);
    try {
      const qs = includeAttachments ? "?include_attachments=true" : "";
      const r = await fetch(`${API}/pg07/cierre/bulk-zip${qs}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(selectedIds),
      });
      if (!r.ok) { toast.error("Error generando ZIP"); return; }
      const attachCount = r.headers.get("X-Attachments-Count");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PG07_cierres_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const extra = includeAttachments && attachCount && parseInt(attachCount) > 0 ? ` + ${attachCount} firma(s)` : "";
      toast.success(`${selectedIds.length} PDFs descargados${extra}`);
    } finally { setZipping(false); }
  };

  return (
    <div className="space-y-6" data-testid="cierre-dashboard-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">PG-07</div>
        <h1 className="text-3xl font-black flex items-center gap-2"><Award className="w-7 h-7 text-emerald-600" /> Cierre Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Proyectos en fase Completado y estado de su ficha de cierre.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cierre-dashboard-stats">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Completados</div><div className="text-3xl font-black">{data.stats.total_completed}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Necesitan acción</div><div className="text-3xl font-black text-red-700">{data.stats.needs_action}</div><div className="text-[10px] text-muted-foreground mt-0.5">0% de cierre · 30+ días</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">En gracia</div><div className="text-3xl font-black text-amber-600">{data.stats.grace_period}</div><div className="text-[10px] text-muted-foreground mt-0.5">0% de cierre · &lt;30 días, aún no urge</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Cierre completo</div><div className="text-3xl font-black text-emerald-700">{data.stats.complete}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Días medios</div><div className="text-3xl font-black text-slate-700">{data.stats.avg_days_since ?? "—"}</div></CardContent></Card>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {[["all", `Todos (${data.rows.length})`], ["needs_action", `🚨 Necesitan acción (${data.stats.needs_action})`], ["complete", `✅ Completos (${data.stats.complete})`]].map(([k, l]) => (
          <Button key={k} variant={filter === k ? "default" : "outline"} size="sm" onClick={() => setFilter(k)} data-testid={`cierre-filter-${k}`}>{l}</Button>
        ))}
        <div className="flex-1"></div>
        {selectedIds.length > 0 && (
          <>
            <span className="text-xs text-muted-foreground">{selectedIds.length} seleccionado(s)</span>
            <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer" data-testid="cierre-include-attachments-label">
              <input type="checkbox" checked={includeAttachments} onChange={e => setIncludeAttachments(e.target.checked)} data-testid="cierre-include-attachments-check" />
              Incluir firmas adjuntas
            </label>
            <Button size="sm" onClick={downloadZip} disabled={zipping} data-testid="cierre-bulk-zip-btn">
              {zipping ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Package className="w-3 h-3 mr-1" />}
              Descargar ZIP ({selectedIds.length})
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected({})} data-testid="cierre-bulk-clear">Limpiar</Button>
          </>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="cierre-dashboard-table">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-8 px-2">
                  <input type="checkbox"
                    checked={rows.length > 0 && rows.every(r => selected[r.project_id])}
                    onChange={e => setSelected(e.target.checked ? Object.fromEntries(rows.map(r => [r.project_id, true])) : {})}
                    data-testid="cierre-select-all" />
                </th>
                <th className="text-left px-4 py-2">Proyecto</th>
                <th className="text-left px-3 py-2">PM</th>
                <th className="text-left px-3 py-2">Días completado</th>
                <th className="text-center px-3 py-2">Propuesta</th>
                <th className="text-center px-3 py-2">Resultados</th>
                <th className="text-center px-3 py-2">Lecciones</th>
                <th className="text-center px-3 py-2">Próximos</th>
                <th className="text-left px-3 py-2">% cierre</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.project_id} className={`border-t hover:bg-slate-50 dark:hover:bg-slate-800/50 ${r.needs_action ? "bg-red-50/40 dark:bg-red-950/10" : ""} ${selected[r.project_id] ? "bg-violet-50/50 dark:bg-violet-950/20" : ""}`} data-testid={`cierre-row-${i}`}>
                  <td className="px-2 text-center">
                    <input type="checkbox" checked={!!selected[r.project_id]}
                      onChange={e => setSelected(prev => ({ ...prev, [r.project_id]: e.target.checked }))}
                      data-testid={`cierre-row-check-${i}`} />
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {r.project_name}
                    {r.needs_action && <Badge className="ml-2 bg-red-100 text-red-700 text-[9px]">🚨 acción</Badge>}
                    {r.grace_period && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[9px]" title="0% de cierre pero aún dentro de los 30 días de margen desde que pasó a Completado" data-testid={`cierre-grace-badge-${i}`}>⏳ en gracia</Badge>}
                  </td>
                  <td className="px-3 py-3 text-xs">{r.pm_email || r.pm_name || "—"}</td>
                  <td className="px-3 py-3 text-xs">
                    {r.days_since_completado != null ? (
                      <span className={r.days_since_completado >= 30 ? "text-red-700 font-semibold" : "text-slate-600"}>
                        {r.days_since_completado} días
                      </span>
                    ) : "—"}
                  </td>
                  {[r.has_propuesta, r.has_resultados, r.has_lecciones, r.has_next].map((v, k) => (
                    <td key={k} className="text-center px-3 py-3">
                      {v ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" /> : <AlertCircle className="w-4 h-4 text-slate-300 mx-auto" />}
                    </td>
                  ))}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-200 rounded overflow-hidden w-20">
                        <div className={`h-full ${r.cierre_pct === 100 ? "bg-emerald-500" : r.cierre_pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${r.cierre_pct}%` }} />
                      </div>
                      <span className="text-xs font-mono">{r.cierre_pct}%</span>
                      {r.pct_override != null && <span className="text-[9px] text-violet-600" title="% fijado manualmente, no calculado de las 4 casillas" data-testid={`cierre-override-badge-${i}`}>manual</span>}
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/pg07/extras?project=${r.project_id}`)} data-testid={`cierre-open-${i}`}>
                      <ClipboardCheck className="w-3 h-3 mr-1" />{r.cierre_pct === 100 ? "Ver / Editar" : "Rellenar"}
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!rows.length && (<tr><td colSpan={10} className="text-center py-10 text-muted-foreground">Sin proyectos en este filtro.</td></tr>)}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
